/**
 * Keep AI layout card production aligned with the live Production panel after shade
 * recalculation. Lives under `helpers/` so Three-JS viewer code can import it without
 * pulling in the dashboard module graph (avoids circular imports with EditorDashboard).
 */

import { useViewerStore } from '../store/store';

const CATEGORY_MAIN_KEY_PREFIX = '__category_main__:';

function getCategoryMainLayoutKey(category: string): string {
    return `${CATEGORY_MAIN_KEY_PREFIX}${category}`;
}

function readLiveViewerMetrics(): {
    modules: number;
    production: number;
    multiplier: number | null;
} {
    const state = useViewerStore.getState() as any;
    return {
        modules: typeof state.modeCount === 'number' && Number.isFinite(state.modeCount) ? state.modeCount : 0,
        production:
            typeof state.actualWattage === 'number' && Number.isFinite(state.actualWattage)
                ? state.actualWattage
                : 0,
        multiplier:
            typeof state.moduleMultiplierAverage === 'number' && Number.isFinite(state.moduleMultiplierAverage)
                ? state.moduleMultiplierAverage
                : null,
    };
}

function readExpectedCardMetrics(
    layoutKey: string,
    categoryLabel?: string,
): {
    key: string;
    modules: number | null;
    production: number | null;
    multiplier: number | null;
    efficiency: number | null;
    solarAccess: number | null;
} {
    const state = useViewerStore.getState() as any;
    const prod = (state.layoutProductionByKey ?? {}) as Record<string, number>;
    const mods = (state.layoutModulesByKey ?? {}) as Record<string, number>;
    const metrics = (state.layoutMetricsByKey ?? {}) as Record<
        string,
        { multiplier?: number | null; efficiency?: number | null; solarAccess?: number | null }
    >;

    const catKey = categoryLabel ? getCategoryMainLayoutKey(categoryLabel) : undefined;
    const key =
        catKey && (prod[catKey] != null || mods[catKey] != null)
            ? catKey
            : prod[layoutKey] != null || mods[layoutKey] != null
              ? layoutKey
              : (catKey ?? layoutKey);

    const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const metricRow = metrics[key];
    return {
        key,
        modules: num(mods[key]),
        production: num(prod[key]),
        multiplier: num(metricRow?.multiplier),
        efficiency: num(metricRow?.efficiency),
        solarAccess: num(metricRow?.solarAccess),
    };
}

/** True when a tree-cutting scenario has trees ghosted (not the base layout card). */
function hasActiveTreeCutScenarioGhost(viewer: unknown): boolean {
    const children = (viewer as { treeGroup?: { children?: unknown[] } })?.treeGroup?.children;
    if (!Array.isArray(children)) return false;
    return children.some((group) => {
        const g = group as { delStatus?: boolean; userData?: { manualGhostFrozen?: boolean } };
        return Boolean(g.delStatus && g.userData?.manualGhostFrozen);
    });
}

/** Resolve layout / category keys for the AI design currently applied in the viewer. */
export function resolveActiveAiDesignApplyKeys(): {
    layoutKey: string;
    categoryLabel?: string;
} | null {
    const state = useViewerStore.getState() as any;
    if (!state.isAiGeneratedDesign) return null;

    const selectionKey = String(state.selectedRoofCombinationKey ?? 'default').trim() || 'default';
    if (selectionKey === 'default') {
        return { layoutKey: 'default' };
    }

    const categoryMain = (state.categoryMainLayoutByCategory ?? {}) as Record<string, { key?: string }>;
    const candidatesByCategory = (state.layoutCandidatesByCategory ?? {}) as Record<
        string,
        Array<{ key?: string }>
    >;

    let categoryLabel: string | undefined;
    if (categoryMain[selectionKey]) {
        categoryLabel = selectionKey;
    } else {
        const match = Object.keys(categoryMain).find(
            (cat) => getCategoryMainLayoutKey(cat) === selectionKey,
        );
        categoryLabel = match ?? selectionKey;
    }

    const main = categoryLabel ? categoryMain[categoryLabel] : undefined;
    const candidates = categoryLabel ? candidatesByCategory[categoryLabel] : undefined;
    const layoutKey = main?.key ?? candidates?.[0]?.key ?? selectionKey;

    return { layoutKey, categoryLabel };
}

export function syncLiveViewerMetricsFromCard(layoutKey: string, categoryLabel?: string): boolean {
    const card = readExpectedCardMetrics(layoutKey, categoryLabel);
    if (card.modules == null || card.modules <= 0) return false;
    if (card.production == null || card.production <= 0) return false;

    const live = readLiveViewerMetrics();
    if (live.modules !== card.modules) return false;

    const patch: Record<string, number> = {
        actualWattage: card.production,
    };
    // AI cards store moduleMultiplierAverage. The Production panel, finalize overlay,
    // and oneDRAW save use the roof-weighted `multiplier` from SetModuleGraph. Copying
    // the card value here showed the wrong multiplier until a hard refresh reloaded
    // the backend figure.
    if (card.efficiency != null && card.efficiency > 0) {
        patch.efficiency = Math.round(card.efficiency);
    }
    if (card.solarAccess != null && card.solarAccess > 0) {
        patch.weightedSolarAverage = Math.round(card.solarAccess);
    }

    useViewerStore.setState(patch as any);
    return true;
}

/**
 * Re-align the Production panel with the active AI layout card after shade / module-graph
 * recalculation (refresh shade, save prep, etc.). Skipped when modules diverge or a tree-cut
 * scenario is active.
 */
export function syncActiveAiDesignProductionFromCard(): boolean {
    const state = useViewerStore.getState() as any;
    if (!state.isAiGeneratedDesign) return false;
    if (hasActiveTreeCutScenarioGhost(state.sceneViewer)) return false;

    const keys = resolveActiveAiDesignApplyKeys();
    if (!keys) return false;
    return syncLiveViewerMetricsFromCard(keys.layoutKey, keys.categoryLabel);
}
